import React, { useCallback, useEffect, useMemo, useState } from 'react'
import tw from 'twin.macro'
import { useSelector } from 'react-redux'
import { RouteComponentProps } from 'react-router-dom'
import { type Address } from 'viem'
import { useWriteContract } from 'wagmi'
import BN from 'bignumber.js'
import {
  Currency,
  CurrencyAmount,
  FactoryVersion,
  JSBI,
  ROUTERS_MAP,
  RouterVersion,
  Token,
  WNATIVE,
} from '@nominex/nomiswap-sdk'
import { AddIcon, CardBody, Message, useModal } from '@nominex/dex-uikit'
import { SlButton } from '@shoelace-style/shoelace/dist/react'

import { useTranslation } from 'contexts/Localization'

import AnimatedIcon from 'components/AnimatedIcon'
import UnsupportedCurrencyFooter from 'components/UnsupportedCurrencyFooter'
import { AutoColumn, ColumnCenter } from 'components/Layout/Column'
import { ConfirmationModalContent } from 'components/TransactionConfirmationModal'
import AddLiquidityModal from 'components/TransactionConfirmationModal/AddLiquidityModal'
import { CurrencyPairImage } from 'components/CurrencyImage'
import CurrencySelectionPanel from 'components/CurrencySelectionPanel'
import { AppBackwardLink, AppBody, AppTitle } from 'components/App'
import { MinimalPositionCard } from 'components/PositionCard'
import ConnectWalletButton from 'components/ConnectWalletButton'
import Dots from 'components/Loader/Dots'
import PoolPriceBar from 'components/PoolPriceBar'
import { STABLE_SWAP_PAIRS } from 'config/constants/exchange'
import { useIsTransactionUnsupported } from 'hooks/Trades'
import { useGasPrice, useWagmi } from 'libs/wagmi'
import { USER_DENIED_SIGNATURE } from 'libs/wagmi/errors'
import { PairState } from 'hooks/usePairs'
import { useCurrency } from 'hooks/Tokens'
import { ApprovalState, useApproveCallback } from 'hooks/useApproveCallback'
import useTransactionDeadline from 'hooks/useTransactionDeadline'
import useFactoryVersionFromURL from 'hooks/useFactoryVersionFromURL'

import { useAppDispatch } from 'state'
import { State } from 'state/types'
import { Field, resetMintState } from 'state/mint/actions'
import {
  useCustomProportionToAddLiquidity,
  useDerivedMintInfo,
  useMintActionHandlers,
  useMintState,
} from 'state/mint/hooks'
import { useTransactionAdder } from 'state/transactions/hooks'
import { useIsExpertMode, useUserSlippageTolerance } from 'state/user/hooks'
import { usePollFarmsPublicData } from 'state/farms/hooks'
// TODO: Restore after migrating to wagmi
// import { useAddLiquidityCallback, LiquidityCallbackState } from 'hooks/useLiquidityCallback'

import { calculateSlippageAmount, isPairWithCurrenciesExist } from 'utils'
import { maxAmountSpend } from 'utils/maxAmountSpend'
import { wrappedCurrency } from 'utils/wrappedCurrency'
import { currencyId } from 'utils/currency'
import { logAmplitudeEvent, logGtmEvent } from 'utils/analytics'
import { formatNumber } from 'utils/formatters'
import { getBalanceAmount, getDecimalAmount } from 'utils/formatBalance'

import { RowBetween } from 'components/Layout'
import CustomProportionWidget from 'components/AddLiquidity/CustomProportionWidget'
import Page from '../Page'
import INomiswapRouter04 from 'config/abi/INomiswapRouter04.json'

const getDecimalFormattedAmount = (amount, decimals) => getDecimalAmount(amount, decimals).toFixed(0, BN.ROUND_DOWN)

const StyledColumnCenter = tw(ColumnCenter)`
  absolute top-[50%] left-[50%] z-50 translate-x-[-50%] translate-y-[-50%]
`

export default function AddLiquidity({
  match: {
    params: { currencyIdA, currencyIdB },
  },
  history,
}: RouteComponentProps<{ currencyIdA?: string; currencyIdB?: string }>) {
  const urlSearchParams = new URLSearchParams(history.location.search)
  const { referredBy } = useSelector((state: State) => state.profile.accountInfo)
  const { account, chainId } = useWagmi()
  const factoryVersion = useFactoryVersionFromURL()
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const { writeContractAsync } = useWriteContract()
  const addTransaction = useTransactionAdder()
  const [addInCustomProportion, setAddInCustomProportion] = useState(false)

  const currencyA = useCurrency(currencyIdA)
  const currencyB = useCurrency(currencyIdB)

  const stableSwapPairs = useMemo(() => STABLE_SWAP_PAIRS[chainId] ?? [], [chainId])
  const isStableSwapPool = useMemo(
    () => isPairWithCurrenciesExist(stableSwapPairs, currencyA, currencyB),
    [currencyA, currencyB, stableSwapPairs],
  )

  usePollFarmsPublicData()

  useEffect(() => {
    if (!currencyIdA && !currencyIdB) {
      dispatch(resetMintState())
    }
  }, [dispatch, currencyIdA, currencyIdB])

  useEffect(() => {
    if (isStableSwapPool) {
      if (!urlSearchParams.has('version') || parseInt(urlSearchParams.get('version'), 10) !== FactoryVersion.V2) {
        urlSearchParams.append('version', FactoryVersion.V2.toString())
        history.push({ search: urlSearchParams.toString() })
      }
    } else if (urlSearchParams.has('version')) {
      urlSearchParams.delete('version')
      history.replace({ search: urlSearchParams.toString() })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStableSwapPool, currencyIdA, currencyIdB])

  const oneCurrencyIsWNative = Boolean(
    chainId && ((currencyA && currencyA.equals(WNATIVE[chainId])) || (currencyB && currencyB.equals(WNATIVE[chainId]))),
  )

  const expertMode = useIsExpertMode()

  // mint state
  const { independentField, typedValue, otherTypedValue } = useMintState()
  const { dependentField, currencies, pair, pairState, currencyBalances, parsedAmounts, price, noLiquidity } =
    useDerivedMintInfo(currencyA ?? undefined, currencyB ?? undefined, factoryVersion)

  useEffect(() => {
    if (noLiquidity) {
      setAddInCustomProportion(false)
    }
  }, [noLiquidity, setAddInCustomProportion])

  const { onFieldAInput, onFieldBInput } = useMintActionHandlers(noLiquidity || addInCustomProportion)

  // modal and loading
  const [attemptingTxn, setAttemptingTxn] = useState<boolean>(false) // clicked confirm

  // txn values
  const deadline = useTransactionDeadline() // custom from users settings
  const [allowedSlippage] = useUserSlippageTolerance() // custom from users
  const [txHash, setTxHash] = useState<string>('')

  // get formatted amounts
  const formattedAmounts = {
    [independentField]: typedValue,
    [dependentField]:
      noLiquidity || addInCustomProportion ? otherTypedValue : parsedAmounts[dependentField]?.toSignificant(6) ?? '',
  }

  const {
    amountAToSwap,
    amountBToSwap,
    amountAToAdd,
    amountBToAdd,
    amountAReceived,
    amountBReceived,
    amountAInUsdt,
    amountBInUsdt,
    liquidityMinted,
    poolTokenPercentage,
    poolShareA,
    poolShareB,
    totalAmountInUsdt,
  } = useCustomProportionToAddLiquidity(currencies, formattedAmounts, pair)

  // get the max amounts user can add
  const maxAmounts: { [field in Field]?: CurrencyAmount<Token> } = [Field.CURRENCY_A, Field.CURRENCY_B].reduce(
    (accumulator, field) => ({
      ...accumulator,
      [field]: maxAmountSpend(currencyBalances[field]),
    }),
    {},
  )

  const atMaxAmounts: { [field in Field]?: CurrencyAmount<Token> } = [Field.CURRENCY_A, Field.CURRENCY_B].reduce(
    (accumulator, field) => ({
      ...accumulator,
      [field]: maxAmounts[field]?.equalTo(parsedAmounts[field] ?? '0'),
    }),
    {},
  )

  // check whether the user has approved the router on the tokens
  const [approvalA, approveACallback] = useApproveCallback(
    parsedAmounts[Field.CURRENCY_A],
    ROUTERS_MAP[chainId][RouterVersion.V3],
  )
  const [approvalB, approveBCallback] = useApproveCallback(
    parsedAmounts[Field.CURRENCY_B],
    ROUTERS_MAP[chainId][RouterVersion.V3],
  )

  const routerAddress = useMemo(() => ROUTERS_MAP[chainId]?.[RouterVersion.V3] as Address, [chainId])

  async function onAdd() {
    if (!account || !chainId || !routerAddress) {
      return
    }

    const { [Field.CURRENCY_A]: parsedAmountA, [Field.CURRENCY_B]: parsedAmountB } = parsedAmounts

    if (!parsedAmountA || !parsedAmountB || !currencyA || !currencyB) {
      return
    }

    const formattedAmountA = getDecimalFormattedAmount(formattedAmounts[Field.CURRENCY_A], currencyA.decimals)
    const formattedAmountB = getDecimalFormattedAmount(formattedAmounts[Field.CURRENCY_B], currencyB.decimals)
    const slippage = noLiquidity ? 0 : allowedSlippage

    const amountsMin = {
      [Field.CURRENCY_A]: addInCustomProportion
        ? calculateSlippageAmount(CurrencyAmount.fromRawAmount(currencyA, JSBI.BigInt(amountAToAdd)), slippage)[0]
        : calculateSlippageAmount(parsedAmountA, slippage)[0],
      [Field.CURRENCY_B]: addInCustomProportion
        ? calculateSlippageAmount(CurrencyAmount.fromRawAmount(currencyB, JSBI.BigInt(amountBToAdd)), slippage)[0]
        : calculateSlippageAmount(parsedAmountB, slippage)[0],
    }

    setAttemptingTxn(true)

    try {
      let hash: string

      if (currencyA?.isNative || currencyB?.isNative) {
        const tokenBIsNative = currencyB?.isNative
        const tokenAddress = wrappedCurrency(tokenBIsNative ? currencyA : currencyB, chainId)?.address as Address
        const tokenDesired = BigInt(
          addInCustomProportion
            ? tokenBIsNative
              ? formattedAmountA
              : formattedAmountB
            : (tokenBIsNative ? parsedAmountA : parsedAmountB).quotient.toString(),
        )
        const tokenMin = BigInt(amountsMin[tokenBIsNative ? Field.CURRENCY_A : Field.CURRENCY_B].toString())
        const ethMin = BigInt(amountsMin[tokenBIsNative ? Field.CURRENCY_B : Field.CURRENCY_A].toString())
        const value = BigInt(
          addInCustomProportion
            ? tokenBIsNative
              ? formattedAmountB
              : formattedAmountA
            : (tokenBIsNative ? parsedAmountB : parsedAmountA).quotient.toString(),
        )

        const functionName = addInCustomProportion ? 'addLiquidityETHImbalanced' : 'addLiquidityETH'

        hash = await writeContractAsync({
          address: routerAddress,
          abi: INomiswapRouter04 as any,
          functionName,
          args: [tokenAddress, tokenDesired, tokenMin, ethMin, account as Address, BigInt(deadline)],
          value,
          chainId,
        } as any)
      } else {
        const tokenA = wrappedCurrency(currencyA, chainId)?.address as Address
        const tokenB = wrappedCurrency(currencyB, chainId)?.address as Address
        const amountADesired = BigInt(addInCustomProportion ? formattedAmountA : parsedAmountA.quotient.toString())
        const amountBDesired = BigInt(addInCustomProportion ? formattedAmountB : parsedAmountB.quotient.toString())
        const amountAMin = BigInt(amountsMin[Field.CURRENCY_A].toString())
        const amountBMin = BigInt(amountsMin[Field.CURRENCY_B].toString())

        const functionName = addInCustomProportion ? 'addLiquidityImbalanced' : 'addLiquidity'

        hash = await writeContractAsync({
          address: routerAddress,
          abi: INomiswapRouter04 as any,
          functionName,
          args: [
            tokenA,
            tokenB,
            amountADesired,
            amountBDesired,
            amountAMin,
            amountBMin,
            account as Address,
            BigInt(deadline),
          ],
          chainId,
        } as any)
      }

      setAttemptingTxn(false)

      const txResponse = { hash } as any
      addTransaction(txResponse, {
        summary: t('Add %amountA% %currencyA% and %amountB% %currencyB%', {
          amountA: addInCustomProportion
            ? formatNumber(getBalanceAmount(amountAToAdd, currencyA.decimals).toNumber())
            : parsedAmountA.toSignificant(3),
          amountB: addInCustomProportion
            ? formatNumber(getBalanceAmount(amountBToAdd, currencyB.decimals).toNumber())
            : parsedAmountB.toSignificant(3),
          currencyA: currencyA.symbol,
          currencyB: currencyB.symbol,
        }),
      })

      setTxHash(hash)

      const investedInUsdt = (amountAInUsdt + amountBInUsdt).toFixed(2)
      const poolName = [currencyA.symbol, currencyB.symbol].sort().join('-')

      logAmplitudeEvent('DefiAddLiquidityPool', {
        referred_by: referredBy,
        invested_in_usdt: investedInUsdt,
        pool: poolName,
        website: 'dex',
      })
      logGtmEvent({ event: 'DefiAddLiquidityPool' })
    } catch (err: any) {
      setAttemptingTxn(false)
      if (err?.code !== USER_DENIED_SIGNATURE && err?.code !== 4001) {
        console.error(err)
      }
    }
  }

  const pendingText = t('Supplying %amountA% %symbolA% and %amountB% %symbolB%', {
    amountA: addInCustomProportion
      ? formatNumber(getBalanceAmount(amountAToAdd, currencyA?.decimals).toNumber(), 'significant')
      : parsedAmounts[Field.CURRENCY_A]?.toSignificant(6) ?? '',
    symbolA: currencies[Field.CURRENCY_A]?.symbol ?? '',
    amountB: addInCustomProportion
      ? formatNumber(getBalanceAmount(amountBToAdd, currencyB?.decimals).toNumber(), 'significant')
      : parsedAmounts[Field.CURRENCY_B]?.toSignificant(6) ?? '',
    symbolB: currencies[Field.CURRENCY_B]?.symbol ?? '',
  })

  const handleCurrencyASelect = useCallback(
    (currencyA_: Currency) => {
      const newCurrencyIdA = currencyId(currencyA_)
      if (newCurrencyIdA === currencyIdB) {
        history.push(`/liquidity/add/${currencyIdB}/${currencyIdA}`)
      } else if (currencyIdB) {
        history.push(`/liquidity/add/${newCurrencyIdA}/${currencyIdB}`)
      } else {
        history.push(`/liquidity/add/${newCurrencyIdA}`)
      }
    },
    [currencyIdB, history, currencyIdA],
  )
  const handleCurrencyBSelect = useCallback(
    (currencyB_: Currency) => {
      const newCurrencyIdB = currencyId(currencyB_)
      if (currencyIdA === newCurrencyIdB) {
        if (currencyIdB) {
          history.push(`/liquidity/add/${currencyIdB}/${newCurrencyIdB}`)
        } else {
          history.push(`/liquidity/add/${newCurrencyIdB}`)
        }
      } else {
        history.push(`/liquidity/add/${currencyIdA || 'BNB'}/${newCurrencyIdB}`)
      }
    },
    [currencyIdA, history, currencyIdB],
  )

  const handleDismissConfirmation = useCallback(() => {
    // if there was a tx hash, we want to clear the input
    if (txHash) {
      onFieldAInput('')
    }
    setTxHash('')
  }, [onFieldAInput, txHash])

  const addIsUnsupported = useIsTransactionUnsupported(currencies?.CURRENCY_A, currencies?.CURRENCY_B)

  const modalHeader = () =>
    noLiquidity ? (
      <div className="text-white space-y-16">
        <h5 className="text-16 font-medium">{t('You will create the pool')}</h5>
        <div className="bg-dark-gray-800 text-24  flex flex-col items-center rounded-12 px-12 py-16">
          <div className="flex items-center gap-12">
            <CurrencyPairImage
              currencyA={currencies[Field.CURRENCY_A]}
              currencyB={currencies[Field.CURRENCY_B]}
              size="24px"
            />
            <div className="text-gray-200 font-medium notranslate" translate="no">{`${currencies[Field.CURRENCY_A]
              ?.symbol}-${currencies[Field.CURRENCY_B]?.symbol}`}</div>
          </div>
        </div>
      </div>
    ) : (
      <div className="text-white space-y-16">
        <h5 className="text-16 font-medium">{t('You will receive')}</h5>
        <div className="bg-dark-gray-800 text-24  flex flex-col items-center rounded-12 px-12 py-16">
          <div className="flex items-center gap-12">
            <CurrencyPairImage
              currencyA={currencies[Field.CURRENCY_A]}
              currencyB={currencies[Field.CURRENCY_B]}
              size="24px"
            />
            <span className="font-medium">
              {liquidityMinted?.toSignificant(6)}{' '}
              {`${currencies[Field.CURRENCY_A]?.symbol}-${currencies[Field.CURRENCY_B]?.symbol} LP`}
            </span>
          </div>
        </div>
        <p className="text-14">
          {t('Output is estimated. If the price changes by more than %slippage% your transaction will revert.', {
            slippage: formatNumber(allowedSlippage / 10000, 'percentage'),
          })}
        </p>
      </div>
    )

  const modalBottom = () => (
    <div className="text-right mt-24">
      <SlButton variant="primary" onClick={() => onAdd()}>
        {noLiquidity ? t('Create pool & supply') : t('Confirm supply')}
      </SlButton>
    </div>
  )

  const [onPresentAddLiquidityModal] = useModal(
    <AddLiquidityModal
      title={noLiquidity ? t('Create pool') : !txHash ? t('Confirm transaction') : ''}
      customOnDismiss={handleDismissConfirmation}
      attemptingTxn={attemptingTxn}
      hash={txHash}
      content={() => <ConfirmationModalContent topContent={modalHeader} bottomContent={modalBottom} />}
      pendingText={pendingText}
      currencyToAdd={pair?.liquidityToken}
      mintedValue={liquidityMinted}
      currencies={currencies}
    />,
    true,
    true,
    'addLiquidityModal',
  )

  let error: string | undefined
  const currencyAAmount = JSBI.BigInt(
    getDecimalFormattedAmount(formattedAmounts[Field.CURRENCY_A], currencyA?.decimals),
  )
  const currencyBAmount = JSBI.BigInt(
    getDecimalFormattedAmount(formattedAmounts[Field.CURRENCY_B], currencyB?.decimals),
  )

  // eslint-disable-next-line default-case
  switch (true) {
    case !account:
      error = t('Connect Wallet')
      break
    case pairState === PairState.INVALID:
      error = t('Invalid pair')
      break
    case !parsedAmounts[Field.CURRENCY_A] || !parsedAmounts[Field.CURRENCY_B]:
      error = t('Enter an amount')
      break
    case !!currencyAAmount && currencyBalances?.[Field.CURRENCY_A]?.lessThan(currencyAAmount):
      error = t('Insufficient %symbol% balance', { symbol: currencies[Field.CURRENCY_A]?.symbol })
      break
    case !!currencyBAmount && currencyBalances?.[Field.CURRENCY_B]?.lessThan(currencyBAmount):
      error = t('Insufficient %symbol% balance', { symbol: currencies[Field.CURRENCY_B]?.symbol })
      break
  }

  const isValid = !error

  return (
    <Page>
      <AppTitle
        title={t('Add Liquidity')}
        subtitle={t('Add liquidity to receive V%version% LP tokens', { version: factoryVersion })}
        alignItems="center"
        maxWidth="532px"
        mb="24px"
      />
      <AppBody>
        <CardBody p="0">
          <div className="flex">
            <AppBackwardLink
              marginTop="20px"
              marginBottom="16px"
              title={t('Liquidity')}
              subtitle={t('Go back to liquidity')}
              route="/liquidity"
            />
          </div>

          <div className="flex flex-col gap-20">
            {noLiquidity && (
              <Message variant="warning">
                <div className="text-14 space-y-8 w-full">
                  <h5 className="text-16 font-medium">{t('You are the first liquidity provider.')}</h5>
                  <p>
                    {t(
                      'The ratio of tokens you add will set the price of this pool. Once you are happy with the rate click supply to review.',
                    )}
                  </p>
                </div>
              </Message>
            )}
            <div className="flex flex-col">
              <CurrencySelectionPanel
                value={formattedAmounts[Field.CURRENCY_A]}
                onUserInput={onFieldAInput}
                onMax={() => {
                  onFieldAInput(maxAmounts[Field.CURRENCY_A]?.toExact() ?? '')
                }}
                onCurrencySelect={handleCurrencyASelect}
                showMaxButton={!atMaxAmounts[Field.CURRENCY_A]}
                currency={currencies[Field.CURRENCY_A]}
                id="add-liquidity-input-tokena"
                showCommonBases
              />
              <div className="relative my-8">
                <StyledColumnCenter>
                  <AnimatedIcon my="8px">
                    <AddIcon width="16px" />
                  </AnimatedIcon>
                </StyledColumnCenter>
              </div>
              <CurrencySelectionPanel
                value={formattedAmounts[Field.CURRENCY_B]}
                onUserInput={onFieldBInput}
                onCurrencySelect={handleCurrencyBSelect}
                onMax={() => {
                  onFieldBInput(maxAmounts[Field.CURRENCY_B]?.toExact() ?? '')
                }}
                showMaxButton={!atMaxAmounts[Field.CURRENCY_B]}
                currency={currencies[Field.CURRENCY_B]}
                id="add-liquidity-input-tokenb"
                showCommonBases
              />
            </div>

            {!noLiquidity && (
              <CustomProportionWidget
                {...{
                  onAddInCustomProportionChange: setAddInCustomProportion,
                  addInCustomProportion,
                  currencies,
                  amountAToSwap,
                  amountBToSwap,
                  amountAToAdd,
                  amountBToAdd,
                  amountAReceived,
                  amountBReceived,
                  poolShareA,
                  poolShareB,
                  totalAmountInUsdt,
                }}
              />
            )}

            {currencies[Field.CURRENCY_A] && currencies[Field.CURRENCY_B] && pairState !== PairState.INVALID && (
              <PoolPriceBar
                currencies={currencies}
                poolTokenPercentage={poolTokenPercentage}
                noLiquidity={noLiquidity}
                price={price}
              />
            )}

            {addIsUnsupported ? (
              <SlButton disabled size="large" variant="primary">
                {t('Unsupported Asset')}
              </SlButton>
            ) : !account ? (
              <ConnectWalletButton />
            ) : (
              <AutoColumn gap="md">
                {(approvalA === ApprovalState.NOT_APPROVED ||
                  approvalA === ApprovalState.PENDING ||
                  approvalB === ApprovalState.NOT_APPROVED ||
                  approvalB === ApprovalState.PENDING) &&
                  isValid && (
                    <RowBetween>
                      {approvalA !== ApprovalState.APPROVED && (
                        <SlButton
                          size="large"
                          variant="primary"
                          onClick={approveACallback}
                          disabled={approvalA === ApprovalState.PENDING}
                          className={approvalB !== ApprovalState.APPROVED ? 'w-[48%]' : 'w-[100%]'}
                        >
                          {approvalA === ApprovalState.PENDING ? (
                            <Dots>{t('Enabling %asset%', { asset: currencies[Field.CURRENCY_A]?.symbol })}</Dots>
                          ) : (
                            t('Enable %asset%', { asset: currencies[Field.CURRENCY_A]?.symbol })
                          )}
                        </SlButton>
                      )}
                      {approvalB !== ApprovalState.APPROVED && (
                        <SlButton
                          size="large"
                          variant="primary"
                          onClick={approveBCallback}
                          disabled={approvalB === ApprovalState.PENDING}
                          className={approvalA !== ApprovalState.APPROVED ? 'w-[48%]' : 'w-[100%]'}
                        >
                          {approvalB === ApprovalState.PENDING ? (
                            <Dots>{t('Enabling %asset%', { asset: currencies[Field.CURRENCY_B]?.symbol })}</Dots>
                          ) : (
                            t('Enable %asset%', { asset: currencies[Field.CURRENCY_B]?.symbol })
                          )}
                        </SlButton>
                      )}
                    </RowBetween>
                  )}
                <SlButton
                  size="large"
                  variant={
                    !isValid && !!parsedAmounts[Field.CURRENCY_A] && !!parsedAmounts[Field.CURRENCY_B]
                      ? 'danger'
                      : 'primary'
                  }
                  onClick={() => {
                    if (expertMode) {
                      onAdd()
                    } else {
                      onPresentAddLiquidityModal()
                    }
                  }}
                  disabled={!isValid || approvalA !== ApprovalState.APPROVED || approvalB !== ApprovalState.APPROVED}
                >
                  {error ?? t('Supply')}
                </SlButton>
              </AutoColumn>
            )}
          </div>
        </CardBody>
      </AppBody>

      {!addIsUnsupported ? (
        pair && !noLiquidity && pairState !== PairState.INVALID ? (
          <AutoColumn style={{ minWidth: '20rem', width: '100%', maxWidth: '400px', marginTop: '1rem' }}>
            <MinimalPositionCard showUnwrapped={oneCurrencyIsWNative} pair={pair} />
          </AutoColumn>
        ) : null
      ) : (
        <UnsupportedCurrencyFooter currencies={[currencies.CURRENCY_A, currencies.CURRENCY_B]} />
      )}
    </Page>
  )
}
